import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Resizable } from 're-resizable';
import { BsPlusLg } from 'react-icons/bs';
import { AiOutlineMail } from 'react-icons/ai';

import { Link } from 'core/routing';
import { FrameHeaderHeight, OldFrameSidebarWidth } from 'styles/constants';

import {
  NotificationBadge,
  // Fallback,
  Heading,
  NavTab,
  MenuItem,
  Menu,
  Logo,
  Icon,
  Box,
  Dialog,
} from 'components';
import { modalStore } from 'apps/modals/app';
import { Menu as ModalMenu } from 'apps/modals/menu';

import { analytics } from 'core/analytics';
import { useClientStore, useRouterStore } from 'apps/hooks';

import { notificationCenter } from 'apps/notificationCenter/appManager';
import { NotificationCenter } from 'apps/notificationCenter';
import { frameStore } from 'apps/components/Frame/appManager';
import { requestNotificationPermission } from 'apps/utils';
import { staticURL } from 'core/url';
import mainTheme from 'styles/themes/wikifactory';

import Inbox from 'apps/Inbox';
import {
  getPlatformFromApp,
  isInDesktopMode,
  listenForInbox,
  listenForNotification,
  listenForPlusIcon,
  menuPopup,
  socialAction,
  windowOperation,
} from 'libs/gutClient/electronBridge';

import { DefaultMenuRoute } from 'constants/global';
import { useGetSystemCondition } from 'hooks';
import { useDesktopCompatibility } from 'hooks/useDesktopCompatible';
import ExitBanner from 'apps/components/ExitBanner';
import * as Icons from '../icons';
import { FrameHeaderStyled, FrameHeaderTitle } from './frameHeader.styled';
import { conditionalCommunityRoutes } from '../config';
import { calcDesktopHeaderMarginLeft } from './config';
import { DownloadAppBtn } from './DownloadAppBtn';

const desktopMode = isInDesktopMode();
const headerBarHeight = 32;

const minInboxWidth = 250;
const maxInboxWidth = 500;

const baseBoxStyles = {
  display: 'flex',
  alignItems: 'center',
};
const wrapperBoxStyle = { display: 'flex', flex: 1 };

const DragSpace = () => {
  return (
    <div
      className='flex-1'
      style={{ appRegion: 'drag', height: headerBarHeight }}
    />
  );
};

const FrameHeader = observer(() => {
  const { inboxManager } = frameStore;

  const [avatarEl, setAvatarEl] = useState<null | HTMLElement>(null);
  const inboxElRef = React.useRef<any>(null);
  const notificationElRef = React.useRef<any>(null);
  const [desktopLeftHeaderStyle, setDesktopLeftHeaderStyle] = useState({});
  const [isMaximized, setIsMaximized] = useState<any>(false);
  const [platform, setPlatform] = useState<any>(null);
  const [notificationEl, setNotificationEl] = useState<null | HTMLElement>(
    null,
  );
  const clientStore = useClientStore();
  const routerStore = useRouterStore();
  const {
    isLoggedIn,
    shouldShowInbox,
    shouldShowAddButton,
    shouldShowNotification,
    shouldShowFrameSideBar,
  } = useGetSystemCondition([routerStore.route.name]);
  const compatibleBeforeTab = useDesktopCompatibility({
    feature: 'tab',
    compareOperator: '<',
  });

  const compatibleWithHambuger = useDesktopCompatibility({
    feature: 'winHamburger',
  });

  const compatibleWithTab = useDesktopCompatibility({
    feature: 'tab',
  });

  const [anchorEl, setAnchor] = useState<null | HTMLElement>(null);

  const handleDialogClose = () => {
    inboxManager.ToggleInbox(false);

    if (desktopMode) {
      socialAction({ type: 'inbox', action: 'close' });
    }
  };

  const handleInboxClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationEl(null);
    if (!anchorEl) setAnchor(event.currentTarget);
    inboxManager.ToggleInbox();
  };

  const routeName = routerStore.route.name;

  const isOnboarding = routeName?.startsWith?.('onboarding');

  const shouldShowLogo = !desktopMode;
  const shouldShowExitBanner = clientStore.isImpersonating();

  const isInWindows = desktopMode && platform === 'win32';
  const shouldShowHamburgerAndWindowsTool =
    isInWindows && compatibleBeforeTab && compatibleWithHambuger;

  let username: string = null;

  if (clientStore?.signedInProfile) {
    ({ username } = clientStore.signedInProfile);
  }

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    handleDialogClose();

    if (desktopMode && compatibleWithTab) {
      socialAction({ type: 'notification', action: 'open' });
    }

    if (notificationEl) {
      setNotificationEl(null);
    } else {
      setNotificationEl(event.currentTarget);
      requestNotificationPermission();
    }
  };

  const closeProfileMenu = () => {
    setAvatarEl(null);
  };

  const closeNotificationMenu = () => {
    setNotificationEl(null);

    if (desktopMode && compatibleWithTab) {
      socialAction({ type: 'notification', action: 'close' });
    }
  };

  const handlePlusClick = () => {
    modalStore.SetModalComponent(ModalMenu);
    analytics.sendEvent('clicked-main-menu');
  };

  const handleSignOut = () => {
    clientStore.SignOutUser();
  };

  const navigateToHome = () => {
    if (isLoggedIn) {
      routerStore.navigate(DefaultMenuRoute, {
        spacePath: username,
      });

      return;
    }

    routerStore.navigate('home');
  };

  useEffect(() => {
    if (!compatibleWithTab) {
      return;
    }

    if (desktopMode) {
      listenForInbox((_, data) => {
        if (data.type === 'close') {
          inboxManager.ToggleInbox(false);
          return;
        }

        inboxManager.ToggleInbox(true);
        closeNotificationMenu();
      });

      listenForNotification((_, { type }: { type: 'open' | 'close' }) => {
        if (type === 'close') {
          closeNotificationMenu();
          return;
        }
        handleNotificationClick({ currentTarget: notificationElRef.current });
      });

      listenForPlusIcon((_, { type }: { type: 'open' | 'close' }) => {
        if (type === 'close') {
          modalStore.ClearComponent();
          return;
        }
        handlePlusClick();
      });
    }
  }, [desktopMode, compatibleWithTab]);

  useEffect(() => {
    const fetchMarginLeft = async () => {
      const marginLeft = await calcDesktopHeaderMarginLeft();
      setDesktopLeftHeaderStyle(desktopMode ? { marginLeft } : {});
    };

    const setupMaxisized = async () => {
      setIsMaximized(await windowOperation('isWindowMaximized'));
    };

    const setupPlatform = async () => {
      setPlatform(await getPlatformFromApp());
    };

    if (desktopMode && compatibleBeforeTab) {
      fetchMarginLeft();

      if (compatibleWithHambuger) {
        setupMaxisized();
        setupPlatform();
      }
    }
  }, [desktopMode, compatibleBeforeTab, compatibleWithHambuger]);

  useEffect(() => {
    if (desktopMode) {
      socialAction({
        type: 'inbox',
        action: inboxManager.isInboxOpen ? 'open' : 'close',
      });

      socialAction({
        type: 'notification',
        action: notificationEl ? 'open' : 'close',
      });
    }
  }, [inboxManager.isInboxOpen, notificationEl]);

  const notificationsPendingCount = notificationCenter.pendingCount;
  // const notificationsPendingCount = notificationCenter.notificationsConn.arrayApp.apps.reduce(
  //   (acc: number, notification: { read: boolean }) => {
  //     if (!notification.read) return acc + 1;
  //     return acc;
  //   },
  //   0,
  // );
  // TODO: optimize this method
  let title = clientStore.atCommunityRoute ? 'Wikifactory Community' : '';
  // if it's a conditionalCommunityRoute, wait until workspaces are loaded to actually show the title
  // otherwise the title will be shown for a moment before the workspaces are loaded and potentially
  // the system realizes it's not a community route.

  if (
    clientStore.signedInProfile &&
    conditionalCommunityRoutes.includes(routerStore.route.name) &&
    !clientStore.areWorkspacesLoaded
  ) {
    title = '';
  }

  const WindowsTool = () => {
    const clazzName =
      'w-8 h-[32px] flex justify-center items-center select-none';

    return (
      <>
        <div
          className={`${clazzName}  hover:bg-slate-800`}
          onClick={() => {
            windowOperation('minimize');
          }}
        >
          <img src={staticURL('assets/icons/chromeMinimize.svg')} />
        </div>
        <div
          className={`${clazzName}  hover:bg-slate-800`}
          onClick={async () => {
            windowOperation('maxUnmax');

            setIsMaximized(await windowOperation('isWindowMaximized'));
          }}
        >
          {isMaximized ? (
            <img src={staticURL('assets/icons/chromeMaxmizeRevert.svg')} />
          ) : (
            <img src={staticURL('assets/icons/chromeMaxmize.svg')} />
          )}
        </div>
        <div
          className={`${clazzName}  hover:bg-red-600`}
          onClick={() => {
            windowOperation('close');
          }}
        >
          <img src={staticURL('assets/icons/chromeClose.svg')} />
        </div>
      </>
    );
  };

  return (
    <FrameHeaderStyled hide={desktopMode && compatibleWithTab}>
      {shouldShowExitBanner && <ExitBanner />}
      <Box sx={baseBoxStyles}>
        <Box
          sx={{
            ...wrapperBoxStyle,
            ...desktopLeftHeaderStyle,
            justifyContent: 'flex-start',
          }}
        >
          <Box sx={{ ...baseBoxStyles, width: '100%' }}>
            {shouldShowHamburgerAndWindowsTool && (
              <div
                className='w-9 h-7 flex items-center justify-center hover:bg-slate-800'
                style={desktopLeftHeaderStyle}
                onClick={() => {
                  menuPopup();
                }}
              >
                <img src={staticURL('assets/hamburger.svg')} />
              </div>
            )}
            {desktopMode && compatibleBeforeTab && (
              <div
                className={`w-8 h-full flex items-center justify-center ${
                  shouldShowFrameSideBar ? 'bg-white' : 'bg-gray-800'
                }`}
                onClick={() => {
                  navigateToHome();
                }}
              >
                <img
                  width={18}
                  height={18}
                  src={staticURL(
                    shouldShowFrameSideBar
                      ? 'assets/icons/home.svg'
                      : 'assets/icons/home_white.svg',
                  )}
                />
              </div>
            )}
            <Box
              sx={{
                ...baseBoxStyles,
                justifyContent: 'center',
                width: 40,
                height: headerBarHeight,
                flexShrink: 0,
              }}
            >
              {shouldShowLogo && (
                <Link
                  onClick={() => {
                    if (!isOnboarding) {
                      navigateToHome();
                    }
                  }}
                  dataTestId='Home'
                >
                  <Logo size='20px' negativeVersion />
                </Link>
              )}
            </Box>
            <DragSpace />
          </Box>
        </Box>
        {title && (
          <Box sx={{ ...wrapperBoxStyle, justifyContent: 'center' }}>
            <FrameHeaderTitle>{title}</FrameHeaderTitle>
          </Box>
        )}
        <Box sx={{ ...wrapperBoxStyle, justifyContent: 'flex-end' }}>
          <DragSpace />
          <Box sx={baseBoxStyles}>
            <Box sx={baseBoxStyles} mr={2}>
              {!desktopMode && <DownloadAppBtn />}
              {shouldShowAddButton && (
                <NavTab
                  size='small'
                  data-testid='new-project'
                  onClick={handlePlusClick}
                >
                  <BsPlusLg size={16} />
                </NavTab>
              )}
              {shouldShowInbox && (
                <NavTab
                  size='small'
                  active={inboxManager?.isInboxOpen}
                  ref={inboxElRef}
                  onClick={handleInboxClick}
                  data-for='tabsTooltip'
                  data-tip='Inbox'
                  data-testid='Inbox'
                  data-place='left'
                >
                  <AiOutlineMail size={16} />
                </NavTab>
              )}
              {shouldShowNotification && (
                <>
                  <NavTab
                    size='small'
                    ref={notificationElRef}
                    onClick={handleNotificationClick}
                    active={Boolean(notificationEl)}
                    data-testid='notification-button'
                  >
                    <NotificationBadge value={notificationsPendingCount}>
                      <Icon name='notification' />
                    </NotificationBadge>
                  </NavTab>
                </>
              )}
            </Box>
            {shouldShowHamburgerAndWindowsTool && (
              <div className='h-[32px] w-px bg-gray-500 ml-2' />
            )}
          </Box>
          {shouldShowHamburgerAndWindowsTool && <WindowsTool />}
        </Box>
      </Box>
      <Menu
        open={Boolean(notificationEl)}
        anchorEl={notificationEl}
        placement='bottom-end'
        sx={{
          boxShadow: '0px 2px 2px gray',
        }}
        onClose={closeNotificationMenu}
        data-testid='notification-menu'
      >
        <NotificationCenter onClose={closeNotificationMenu} visible />
      </Menu>
      <Menu
        open={Boolean(avatarEl)}
        anchorEl={avatarEl}
        onClose={closeProfileMenu}
        onClick={closeProfileMenu}
        sx={{
          boxShadow: '1px 2px 7px gray',
        }}
        data-testid='profile-menu'
      >
        <Box p={2}>
          <Heading size='small'>{username}</Heading>
        </Box>
        <Link name='profile' params={{ profile: username }}>
          <MenuItem>
            <Box mr={2}>
              <Icons.Profile />
            </Box>
            Community Profile
          </MenuItem>
        </Link>
        <Link
          name='workspace.spaceSettings.account'
          params={{ spacePath: username }}
        >
          <MenuItem>
            <Box mr={2}>
              <Icons.Settings />
            </Box>
            Settings
          </MenuItem>
        </Link>
        <MenuItem data-testid='top-menu-sign-out' onClick={handleSignOut}>
          <Box mr={2}>
            <Icons.SignOut />
          </Box>
          Sign Out
        </MenuItem>
      </Menu>

      <Dialog
        onClose={handleDialogClose}
        open={inboxManager.isInboxOpen}
        placement='left-top'
        anchorEl={anchorEl}
        offsetBottom={FrameHeaderHeight}
        overlayStyle={{
          top: desktopMode && compatibleWithTab ? 0 : FrameHeaderHeight,
          left: OldFrameSidebarWidth,
        }}
        sx={{
          overflow: 'hidden',
          position: 'fixed',
          ...(desktopMode && compatibleWithTab ? { right: 0 } : {}),
        }}
      >
        <Resizable
          minWidth={minInboxWidth}
          maxWidth={maxInboxWidth}
          enable={{ right: true }}
          defaultSize={{ width: maxInboxWidth, height: '100%' }}
          style={{ background: mainTheme.color.wfWhite }}
        >
          <Inbox onClose={handleDialogClose} />
        </Resizable>
      </Dialog>
    </FrameHeaderStyled>
  );
});

export default FrameHeader;
